import { type ReactNode } from 'react';

import { type AdminAgentTransientProgress } from '@apps/web/components/admin/use_admin_agent_chat_stream';
import {
    type AdminAgentSubmitMessageOptions,
    type AdminAgentSubmitMessageOutcome,
} from '@apps/web/components/admin/use_admin_agent_run_actions';
import { type ChatInputSubmitMetadata } from '@apps/web/components/phoebe_chat/chat_input';
import {
    type AssistantMessageActionRenderer,
    type ChatInputImageAttachment,
    type MessageArtifactRenderer,
    PhoebeAgentEventFeedChatSurface,
    type PhoebeAgentEventFeedChatViewState,
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolArtifactRenderer,
    type ToolEntryInspectorHandler,
    type ToolOutputRenderer,
} from '@apps/web/components/phoebe_chat_view';
import { IdentifierRedactionProvider } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';

export type AdminAgentChatPanelSession = {
    readonly allowedSelectedRunId: string | null;
    readonly chatState: PhoebeAgentEventFeedChatViewState;
    readonly canSendMessage: boolean;
    readonly decidingCallIds: Record<string, boolean>;
    readonly errorMessage: string | null;
    readonly inspectedToolEntryId: string | null;
    readonly isCreatingRun: boolean;
    readonly isInterrupting: boolean;
    readonly isSending: boolean;
    readonly imageAttachments: ReadonlyArray<ChatInputImageAttachment>;
    readonly imageUploadDisabled: boolean;
    readonly isSelectedRunBusy: boolean;
    readonly renderMessageArtifacts: MessageArtifactRenderer;
    readonly renderToolArtifacts: ToolArtifactRenderer;
    readonly renderToolOutput: ToolOutputRenderer;
    readonly selectedOrganizationApprovalName: string | null;
    readonly selectedOrganizationId: string;
    readonly transientProgress?: AdminAgentTransientProgress | null;
    readonly submitMessage: (
        content: string,
        metadata?: ChatInputSubmitMetadata,
        options?: AdminAgentSubmitMessageOptions,
    ) => Promise<AdminAgentSubmitMessageOutcome>;
    readonly onImageFilesSelected: (files: Array<File>) => void;
    readonly onRemoveImageAttachment: (attachmentId: string) => void;
    readonly decideToolCall: (
        target: ToolApprovalDecisionTarget,
        decision: ToolApprovalDecision,
    ) => Promise<void>;
    readonly interruptSelectedRun: () => Promise<void>;
    readonly setInspectedToolEntryId: (entryId: string | null) => void;
};

export function AdminAgentChatPanel({
    session,
    leadingControl,
    placeholder,
    emptyState,
    emptyComposerPlacement,
    canStopRun,
    showErrorMessage = true,
    onInspectToolEntry,
    renderAssistantMessageActions,
}: {
    readonly session: AdminAgentChatPanelSession;
    readonly leadingControl: ReactNode;
    readonly placeholder: string;
    readonly emptyState: ReactNode;
    readonly emptyComposerPlacement?: 'center' | 'bottom';
    readonly canStopRun: boolean;
    readonly showErrorMessage?: boolean;
    readonly onInspectToolEntry: ToolEntryInspectorHandler;
    readonly renderAssistantMessageActions: AssistantMessageActionRenderer;
}) {
    const combinedLeadingControl = leadingControl ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
            {leadingControl}
        </div>
    ) : null;

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            {showErrorMessage && session.errorMessage ? (
                <div className="text-destructive border-destructive/30 bg-destructive/5 mx-4 mt-3 rounded-md border px-3 py-2 text-xs">
                    {session.errorMessage}
                </div>
            ) : null}
            {session.transientProgress ? (
                <div
                    aria-live="polite"
                    className="text-muted-foreground px-4 pt-3 text-xs sm:px-8"
                    data-testid="admin-agent-live-progress"
                >
                    {session.transientProgress.content}
                </div>
            ) : null}
            <IdentifierRedactionProvider redact={false}>
                <PhoebeAgentEventFeedChatSurface
                    state={session.chatState}
                    disabled={!session.canSendMessage}
                    isSubmitting={session.isSending || session.isCreatingRun}
                    onSubmit={session.submitMessage}
                    imageAttachments={session.imageAttachments}
                    onImageFilesSelected={session.onImageFilesSelected}
                    onRemoveImageAttachment={session.onRemoveImageAttachment}
                    imageUploadDisabled={session.imageUploadDisabled}
                    leadingControl={combinedLeadingControl}
                    submitOnShiftEnter={session.isSelectedRunBusy}
                    placeholder={placeholder}
                    organizationId={session.selectedOrganizationId || null}
                    organizationName={session.selectedOrganizationApprovalName}
                    emptyState={emptyState}
                    emptyComposerPlacement={emptyComposerPlacement}
                    showGeneralOutreachProgressCards={true}
                    isStopping={session.isInterrupting}
                    canStopRun={canStopRun}
                    onStopRun={session.interruptSelectedRun}
                    decidingCallIds={session.decidingCallIds}
                    onToolDecision={session.decideToolCall}
                    renderMessageArtifacts={session.renderMessageArtifacts}
                    renderToolArtifacts={session.renderToolArtifacts}
                    renderToolOutput={session.renderToolOutput}
                    onInspectToolEntry={onInspectToolEntry}
                    selectedToolEntryId={session.inspectedToolEntryId}
                    toolCardPresentation="minimal"
                    groupActivityEntries={true}
                    renderAssistantMessageActions={
                        renderAssistantMessageActions
                    }
                />
            </IdentifierRedactionProvider>
        </div>
    );
}
